import _ from 'lodash/fp'
import React from 'react'
import axios from 'axios'
import {viewRecord, downloadRecord, RecordClass} from '../utils'
import { processAllFiles } from './processAllFiles'
import {getFileList} from '../utils/fileEventToList'
import {EventLogStatus} from '../utils/recordEnums'
import {appendLog} from './uploadLog'
import { syncControlLock } from './queues'

const FAST_MODE_FAKE_LOCK = {value: [_.identity]}

export const UploaderAction = {
  Retry: ({ recordManager, uploader }, record) => {
    recordManager.queueToRetry(record)
    uploader.start()
  },

  View: (utils, record) => {
    viewRecord(record)
  },
  Download: async ({ recordManager }, record) => {
    recordManager.updateRecord(record, _.set('preparingDownload', true))
    try {
      await downloadRecord(record)
    } catch (e) {
      alert('fail')
    }
    recordManager.updateRecord(record, _.unset('preparingDownload'))
  },

  Delete: async ({ recordManager, uploader }, record) => {
    try {
      recordManager.updateRecord(record, _.set('deleting', true))
      const recordUID = await uploader.cancelRecord(record.key)
      if (recordUID || record.recordUID) {
        await axios.delete(`/record/${recordUID || record.recordUID}`)
      }
      recordManager.removeRecord(record)
    } catch (e) {
      console.log(e)
      recordManager.updateRecord(record, _.set('deleting', false))
    }
  },
  Pause: async ({ recordManager, uploader }, record) => {
    await recordManager.pauseRecord(record.key)
    await uploader.cancelRecord(record.key)
  },
  ConfirmDelete: ({ recordManager }, record) => {
    recordManager.updateRecord(record, _.set('confirmingDelete', true))
  },
  CancelDelete: ({ recordManager }, record) => {
    recordManager.updateRecord(record, _.set('confirmingDelete', false))
  },

  CloseDetailsDialog: ({ setData }, record) => {
    setData(_.set('detailsDialog', false))
  },
  ShowDetailsDialog: async ({ setData, recordManager }, record) => {
    const queuedRecord = recordManager.getRecentlyQuarantinedRecord(record)
    if (queuedRecord) {
      const allFiles = _.uniqBy('file', [
        ...queuedRecord.uploaded,
        ...queuedRecord.files,
        ...queuedRecord.uploading,
      ])
      setData(
        _.set('detailsDialog', {
          ...queuedRecord,
          files: allFiles.map(({ file }) => ({
            name: file.name,
            isHarmful: _.find(['file', file], queuedRecord.quarantined),
          })),
        })
      )
    } else {
      setData(_.set('detailsDialog', { ...record, files: [] }))
      const { data } = await axios.get(`/record/${record.recordUID}/quarantine`)
      setData(
        _.set('detailsDialog', {
          ...record,
          files: data.map(({ originalFilename }) => ({
            name: originalFilename,
            isHarmful: true,
          })),
        })
      )
    }
  },

  Upload: async ({ data, setData, mode, setMode, recordManager, uploader, ...utils }, files) => {
    let allErrors = []
    appendLog(files,EventLogStatus.Waiting)

    function calculateReadSpeed(
      file, 
      chunkSize = 1048576
    ) { // default chunk size 1MB
      // chunkSize = 1048576 * 10 // 10MB
      // chunkSize = 524288  // 0.5MB
    return new Promise((resolve, reject) => {
        const startTime = performance.now();
        const reader = new FileReader();

        const size = Math.min(file.size, chunkSize);

        // Read only the first chunk of the file
        const blob = file.size > chunkSize ? file.slice(0, size) : file;
        
        reader.onloadend = () => {
            const endTime = performance.now();
            const timeTaken = (endTime - startTime) / 1000; // time in seconds
            const sizeInMB = size / (1024 * 1024); // size of the chunk in Megabytes
            const speedMBps = sizeInMB / timeTaken; // speed in MB/s for the chunk

            resolve({speed: speedMBps, size: size / (1024 * 1024)});
        };

        reader.onerror = reject;

        reader.readAsArrayBuffer(blob);
    });
    }

    var slowMode = data.slowMode
    
    var sampleList = files.length <= 10 ? files : files.sort((a,b) => b.size - a.size).slice(0,10)
    const speeds = await Promise.all(sampleList.map(file => calculateReadSpeed(file)));
    const totalSize = speeds.reduce((a, b) => a + b.size, 0);
    const sizeWeightedAverageSpeed = speeds.reduce((total, current) => {
      return total + (current.speed * current.size);
    }, 0) / totalSize;
    const SPEED_DISK_READER = window.SPEED_DISK_READER || 2;
    console.log(`Size-Weighted Average Speed: ${sizeWeightedAverageSpeed.toFixed(2)} MB/s`);

    // Conditions to enter slow mode
    if((sizeWeightedAverageSpeed < SPEED_DISK_READER && files.length > 10)){
      console.log('Slow mode')
      setMode(_.set('slowMode', true))
      slowMode = true
    }else{
      console.log('Fast mode')
      setMode(_.set('slowMode', false))
      slowMode = false
    }
    
    // If slow mode (CD or DVD):
    // Wait for control lock release before processing files
    // const {
    //   value: [unlockControl],
    // } = await (slowMode ?  syncControlLock.next() : FAST_MODE_FAKE_LOCK)
    try{
      /* eslint-disable no-restricted-syntax */
      for await (const [records, discarded, errors] of processAllFiles(files,[RecordClass.Radiology],slowMode)) {
        if (records?.length) {
          recordManager.insertMany(records)
          await utils.resolveJwt()
          uploader.start()
        }
        if (errors?.length) {
          allErrors = allErrors.concat(errors)
        }
        setData(_.update('discarded', (v = []) => v.concat(discarded)))
        appendLog(discarded || [], EventLogStatus.Discarded)
      }
    }
    // release control lock
    finally{
      // unlockControl()
    }
    setData(_.update('isProcessing', i => i - 1))
  },
  UploadFromEvent: async (utils,e) => {
    utils.setData(_.flow(
      _.update('activated',(i=0) => i + 1),
      _.update('isProcessing', i => i + 1)
    ))
    const files = await getFileList(e)
    return UploaderAction.Upload(utils,files)
  },
  GetFilesForRecord: ({ recordManager}, recordKey) => {

    const {files = [],uploading = [],uploaded = [],failed = [], paused = []} =recordManager.getQueuedRecord(recordKey)
    return [...files,...uploading,...uploaded,...failed,...paused].map(_.get('file'))
  },
  Deactivate: (utils,lastActivated) => {
    utils.setData(_.update('activated',(currentActivated) => lastActivated !== currentActivated ? currentActivated : 0))
  }
}
